'use client'

import type { LucideIcon } from 'lucide-react'
import * as React from 'react'
import { CirclePlay } from 'lucide-react'

import { Button } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import type { CardAction } from './card-action'

interface ChallengeCardProps {
  id: string
  title: string
  coverUrl: string
  label?: string
  startAt: Date | string
  endAt: Date | string
  action: CardAction
  ctaLabel?: string
  ctaIcon?: LucideIcon
  className?: string
  lineClamp?: boolean
}

function formatChallengeDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  const datePart = d.toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
  const timePart = d.toLocaleTimeString('id-ID', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
  return `${datePart}; ${timePart}`
}

export function ChallengeCard({
  id,
  title,
  coverUrl,
  label = 'Tantangan spesial buat kamu!',
  startAt,
  endAt,
  action,
  ctaLabel = 'Terima tantangan',
  ctaIcon: CtaIcon = CirclePlay,
  className,
  lineClamp = true,
}: ChallengeCardProps) {
  const formattedStart = formatChallengeDate(startAt)
  const formattedEnd = formatChallengeDate(endAt)

  const ctaContent = (
    <>
      <CtaIcon className='srd:size-5 srd:shrink-0 srd:mr-2' />
      {ctaLabel}
    </>
  )

  return (
    <div
      data-id={id}
      className={cn(
        'srd:flex srd:flex-col srd:sm:flex-row srd:bg-neutral-white srd:rounded-2xl srd:overflow-hidden srd:shadow-md srd:w-full',
        className,
      )}
    >
      {/* Cover image — 4:3 always */}
      <div className='srd:w-full srd:aspect-4/3 srd:sm:w-auto srd:sm:h-full srd:sm:aspect-4/3 srd:shrink-0 srd:overflow-hidden srd:sm:max-w-[320px] srd:p-4 srd:sm:p-0'>
        <img
          src={coverUrl}
          alt={title}
          draggable={false}
          loading='lazy'
          decoding='async'
          className='srd:w-full srd:h-full srd:object-cover srd:rounded-2xl srd:sm:rounded-none srd:sm:max-w-[320px]'
        />
      </div>

      {/* Content */}
      <div className='srd:flex srd:flex-1 srd:flex-col srd:gap-2 srd:p-4 srd:justify-center srd:min-w-0'>
        <div className='srd:flex srd:flex-col'>
          <span className={cn(typographyVariants({ variant: 'title-sm' }), 'srd:text-grey-100')}>{label}</span>
          {/* min-h reserves 2-line height (headline-md: 36px leading × 2) so card height is consistent */}
          <h3
            className={cn(
              typographyVariants({ variant: 'headline-md' }),
              'srd:text-neutral-black srd:line-clamp-2',
              lineClamp && 'srd:min-h-[72px]',
            )}
          >
            {title}
          </h3>
        </div>

        <div className='srd:flex srd:flex-col'>
          <span className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-neutral-black')}>
            Mulai {formattedStart}
          </span>
          <span className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-neutral-black')}>
            Berakhir {formattedEnd}
          </span>
        </div>

        {action.type === 'link' ? (
          <Button variant='gradient' size='default' asChild className='srd:w-fit'>
            {action.renderWrapper(ctaContent)}
          </Button>
        ) : (
          <Button variant='gradient' size='default' className='srd:w-fit' onClick={action.onClick}>
            {ctaContent}
          </Button>
        )}
      </div>
    </div>
  )
}
